/**
 * Multilingual scaffold · Sprint 24 Phase 1 (EN + DE).
 *
 * Design principles:
 *   - English (en) is the default locale and the master language.
 *   - All existing English URLs are preserved unchanged — the scaffold
 *     ONLY adds new /<locale>/ routes, never rewrites existing ones.
 *   - Translations are opt-in per page. If a translation does not exist
 *     for a (locale, slug) pair, the language switcher visibly flags it
 *     as "coming soon" and the English URL remains the authoritative one.
 *   - Each translated page emits reciprocal hreflang alternates + a
 *     self-referencing canonical. English page emits hreflang back to
 *     every translated equivalent plus `x-default` on itself.
 */

export const SUPPORTED_LOCALES = [
  { code: "en", label: "English",   nativeLabel: "English",     dir: "ltr", pathPrefix: "" },
  { code: "de", label: "German",    nativeLabel: "Deutsch",     dir: "ltr", pathPrefix: "/de" },
  { code: "fr", label: "French",    nativeLabel: "Français",    dir: "ltr", pathPrefix: "/fr" },
  { code: "es", label: "Spanish",   nativeLabel: "Español",     dir: "ltr", pathPrefix: "/es" },
  // Phase 2 locales — all active. Untranslated pages render the English
  // master content under the locale prefix (canonical → English) via the
  // LocaleGate fallback in App.js — never a 404.
  { code: "it", label: "Italian",   nativeLabel: "Italiano",    dir: "ltr", pathPrefix: "/it" },
  { code: "nl", label: "Dutch",     nativeLabel: "Nederlands",  dir: "ltr", pathPrefix: "/nl" },
  { code: "sv", label: "Swedish",   nativeLabel: "Svenska",     dir: "ltr", pathPrefix: "/sv" },
  { code: "da", label: "Danish",    nativeLabel: "Dansk",       dir: "ltr", pathPrefix: "/da" },
  { code: "fi", label: "Finnish",   nativeLabel: "Suomi",       dir: "ltr", pathPrefix: "/fi" },
  { code: "ar", label: "Arabic",    nativeLabel: "العربية",     dir: "rtl", pathPrefix: "/ar" },
];

export const ACTIVE_LOCALES = SUPPORTED_LOCALES.filter((l) => !l.comingSoon);
export const DEFAULT_LOCALE = "en";

/** Detect the locale from a pathname. Returns 'en' when no prefix matches. */
export function detectLocale(pathname) {
  const match = (pathname || "/").match(/^\/([a-z]{2})(?:\/|$)/);
  if (!match) return DEFAULT_LOCALE;
  const found = SUPPORTED_LOCALES.find((l) => l.code === match[1] && l.pathPrefix);
  return found ? found.code : DEFAULT_LOCALE;
}

/** Strip the locale prefix from a pathname. */
export function stripLocale(pathname) {
  return (pathname || "/").replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
}

/** Add a locale prefix to a canonical English path. */
export function withLocale(path, locale) {
  if (locale === DEFAULT_LOCALE) return path;
  const loc = SUPPORTED_LOCALES.find((l) => l.code === locale);
  if (!loc || !loc.pathPrefix) return path;
  return `${loc.pathPrefix}${path === "/" ? "" : path}` || "/";
}

/** Shared UI strings. Falls back to English when a key is missing. */
export const UI_STRINGS = {
  en: {
    "lang.selector":        "Language",
    "lang.coming_soon":     "coming soon",
    "lang.switch_to":       "Switch to",
    "nav.about":            "About",
    "nav.services":         "Services",
    "nav.industries":       "Industries",
    "nav.capabilities":     "Capabilities",
    "nav.case_studies":     "Case studies",
    "nav.trends":           "Insights",
    "nav.contact":          "Contact",
    "nav.start_project":    "Start a project",
    "footer.tagline":       "India sourcing · QC · product development · since 2002",
    "footer.rights":        "All rights reserved",
    "cta.start_project":    "Start a project",
    "cta.contact":          "Contact us",
    "page.related":         "Related",
    "page.faqs":            "Frequently asked",
    "translation.pending":  "Full German translation shipping shortly. The authoritative English page is linked below.",
  },
  de: {
    "lang.selector":        "Sprache",
    "lang.coming_soon":     "demnächst",
    "lang.switch_to":       "Wechseln zu",
    "nav.about":            "Über uns",
    "nav.services":         "Dienstleistungen",
    "nav.industries":       "Branchen",
    "nav.capabilities":     "Leistungen",
    "nav.case_studies":     "Fallstudien",
    "nav.trends":           "Insights",
    "nav.contact":          "Kontakt",
    "nav.start_project":    "Projekt starten",
    "footer.tagline":       "Indien-Beschaffung · Qualitätskontrolle · Produktentwicklung · seit 2002",
    "footer.rights":        "Alle Rechte vorbehalten",
    "cta.start_project":    "Projekt starten",
    "cta.contact":          "Kontakt aufnehmen",
    "page.related":         "Verwandte Themen",
    "page.faqs":            "Häufig gestellte Fragen",
    "translation.pending":  "Die vollständige deutsche Übersetzung wird in Kürze veröffentlicht. Die maßgebliche englische Seite ist unten verlinkt.",
  },
  fr: {
    "lang.selector":        "Langue",
    "lang.coming_soon":     "bientôt disponible",
    "lang.switch_to":       "Passer à",
    "nav.about":            "À propos",
    "nav.services":         "Services",
    "nav.industries":       "Secteurs",
    "nav.capabilities":     "Capacités",
    "nav.case_studies":     "Études de cas",
    "nav.trends":           "Insights",
    "nav.contact":          "Contact",
    "nav.start_project":    "Démarrer un projet",
    "footer.tagline":       "Sourcing Inde · Contrôle qualité · Développement produit · depuis 2002",
    "footer.rights":        "Tous droits réservés",
    "cta.start_project":    "Démarrer un projet",
    "cta.contact":          "Nous contacter",
    "page.related":         "Pages liées",
    "page.faqs":            "Questions fréquentes",
    "translation.pending":  "La traduction française complète arrive prochainement. La page anglaise officielle est liée ci-dessous.",
  },
  es: {
    "lang.selector":        "Idioma",
    "lang.coming_soon":     "próximamente",
    "lang.switch_to":       "Cambiar a",
    "nav.about":            "Nosotros",
    "nav.services":         "Servicios",
    "nav.industries":       "Sectores",
    "nav.capabilities":     "Capacidades",
    "nav.case_studies":     "Casos de estudio",
    "nav.trends":           "Insights",
    "nav.contact":          "Contacto",
    "nav.start_project":    "Iniciar un proyecto",
    "footer.tagline":       "Sourcing India · Control de calidad · Desarrollo de producto · desde 2002",
    "footer.rights":        "Todos los derechos reservados",
    "cta.start_project":    "Iniciar un proyecto",
    "cta.contact":          "Contactar",
    "page.related":         "Páginas relacionadas",
    "page.faqs":            "Preguntas frecuentes",
    "translation.pending":  "La traducción completa al español llegará pronto. La página inglesa oficial está enlazada abajo.",
  },
  // Sprint 27 · Phase 2 UI strings (IT, NL, SV, DA, FI, AR).
  it: {
    "lang.selector": "Lingua", "lang.coming_soon": "in arrivo", "lang.switch_to": "Passa a",
    "nav.about": "Chi siamo", "nav.services": "Servizi", "nav.industries": "Settori",
    "nav.capabilities": "Capacità", "nav.case_studies": "Casi studio", "nav.trends": "Insights",
    "nav.contact": "Contatti", "nav.start_project": "Avvia un progetto",
    "footer.tagline": "Sourcing India · Controllo qualità · Sviluppo prodotto · dal 2002",
    "footer.rights": "Tutti i diritti riservati",
    "cta.start_project": "Avvia un progetto", "cta.contact": "Contattaci",
    "page.related": "Pagine correlate", "page.faqs": "Domande frequenti",
    "translation.pending": "La traduzione italiana completa arriverà a breve. La pagina inglese ufficiale è collegata sotto.",
  },
  nl: {
    "lang.selector": "Taal", "lang.coming_soon": "binnenkort", "lang.switch_to": "Wissel naar",
    "nav.about": "Over ons", "nav.services": "Diensten", "nav.industries": "Sectoren",
    "nav.capabilities": "Capaciteiten", "nav.case_studies": "Case studies", "nav.trends": "Insights",
    "nav.contact": "Contact", "nav.start_project": "Project starten",
    "footer.tagline": "Sourcing India · Kwaliteitscontrole · Productontwikkeling · sinds 2002",
    "footer.rights": "Alle rechten voorbehouden",
    "cta.start_project": "Project starten", "cta.contact": "Neem contact op",
    "page.related": "Gerelateerde pagina's", "page.faqs": "Veelgestelde vragen",
    "translation.pending": "De volledige Nederlandse vertaling verschijnt binnenkort. De officiële Engelse pagina is hieronder gelinkt.",
  },
  sv: {
    "lang.selector": "Språk", "lang.coming_soon": "kommer snart", "lang.switch_to": "Byt till",
    "nav.about": "Om oss", "nav.services": "Tjänster", "nav.industries": "Branscher",
    "nav.capabilities": "Kapaciteter", "nav.case_studies": "Fallstudier", "nav.trends": "Insights",
    "nav.contact": "Kontakt", "nav.start_project": "Starta ett projekt",
    "footer.tagline": "Sourcing Indien · Kvalitetskontroll · Produktutveckling · sedan 2002",
    "footer.rights": "Alla rättigheter förbehållna",
    "cta.start_project": "Starta ett projekt", "cta.contact": "Kontakta oss",
    "page.related": "Relaterade sidor", "page.faqs": "Vanliga frågor",
    "translation.pending": "Fullständig svensk översättning kommer snart. Den officiella engelska sidan länkas nedan.",
  },
  da: {
    "lang.selector": "Sprog", "lang.coming_soon": "kommer snart", "lang.switch_to": "Skift til",
    "nav.about": "Om os", "nav.services": "Services", "nav.industries": "Brancher",
    "nav.capabilities": "Kapaciteter", "nav.case_studies": "Casestudier", "nav.trends": "Insights",
    "nav.contact": "Kontakt", "nav.start_project": "Start et projekt",
    "footer.tagline": "Sourcing Indien · Kvalitetskontrol · Produktudvikling · siden 2002",
    "footer.rights": "Alle rettigheder forbeholdes",
    "cta.start_project": "Start et projekt", "cta.contact": "Kontakt os",
    "page.related": "Relaterede sider", "page.faqs": "Ofte stillede spørgsmål",
    "translation.pending": "Fuld dansk oversættelse kommer snart. Den officielle engelske side er linket nedenfor.",
  },
  fi: {
    "lang.selector": "Kieli", "lang.coming_soon": "tulossa pian", "lang.switch_to": "Vaihda kieleen",
    "nav.about": "Tietoa meistä", "nav.services": "Palvelut", "nav.industries": "Toimialat",
    "nav.capabilities": "Kyvyt", "nav.case_studies": "Casetutkimukset", "nav.trends": "Insights",
    "nav.contact": "Yhteystiedot", "nav.start_project": "Aloita projekti",
    "footer.tagline": "Intia-sourcing · Laadunvalvonta · Tuotekehitys · vuodesta 2002",
    "footer.rights": "Kaikki oikeudet pidätetään",
    "cta.start_project": "Aloita projekti", "cta.contact": "Ota yhteyttä",
    "page.related": "Liittyvät sivut", "page.faqs": "Usein kysytyt kysymykset",
    "translation.pending": "Täysi suomenkielinen käännös julkaistaan pian. Virallinen englanninkielinen sivu on linkitetty alla.",
  },
  ar: {
    "lang.selector": "اللغة", "lang.coming_soon": "قريبًا", "lang.switch_to": "التبديل إلى",
    "nav.about": "من نحن", "nav.services": "الخدمات", "nav.industries": "القطاعات",
    "nav.capabilities": "القدرات", "nav.case_studies": "دراسات الحالة", "nav.trends": "رؤى",
    "nav.contact": "اتصل بنا", "nav.start_project": "ابدأ مشروعًا",
    "footer.tagline": "التوريد من الهند · مراقبة الجودة · تطوير المنتج · منذ 2002",
    "footer.rights": "جميع الحقوق محفوظة",
    "cta.start_project": "ابدأ مشروعًا", "cta.contact": "تواصل معنا",
    "page.related": "صفحات ذات صلة", "page.faqs": "الأسئلة الشائعة",
    "translation.pending": "ستتوفر الترجمة العربية الكاملة قريبًا. الصفحة الإنجليزية الرسمية مرتبطة أدناه.",
  },
};

/** Pick a translated UI string, falling back to English. */
export function t(key, locale) {
  const bag = UI_STRINGS[locale] || UI_STRINGS[DEFAULT_LOCALE];
  return bag[key] || UI_STRINGS[DEFAULT_LOCALE][key] || key;
}
